<template>
  <v-container>
    <v-card>
      <v-card-title>Search Page Views</v-card-title>
      <v-card-text>
        <div class="text-overline mb-1" style="color:#898781">Report Period</div>
        <div class="d-flex align-start flex-wrap mb-2">
          <v-select
            v-model="periodYear"
            :items="yearOptions"
            dense
            outlined
            hide-details
            style="max-width:110px"
            class="mr-4 mt-1 flex-grow-0"
            @change="onYearChange"
          ></v-select>
          <v-select
            v-model="selectedMonths"
            :items="monthsForYear"
            item-text="label"
            item-value="value"
            label="Months"
            multiple
            dense
            outlined
            hide-details
            small-chips
            deletable-chips
            clearable
            style="max-width:360px"
            class="mr-4 mt-1 flex-grow-0"
            @change="onMonthsChange"
          ></v-select>
          <v-chip-group class="flex-grow-1">
            <v-chip
              v-if="periodYear === currentYear"
              :color="quick === 'today' ? 'primary' : ''"
              :dark="quick === 'today'"
              :outlined="quick !== 'today'"
              @click="applyQuick('today')"
            >Today</v-chip>
            <v-chip
              v-if="periodYear === currentYear"
              :color="quick === 'week' ? 'primary' : ''"
              :dark="quick === 'week'"
              :outlined="quick !== 'week'"
              @click="applyQuick('week')"
            >Last 7 days</v-chip>
            <v-chip
              :color="quick === 'year' ? 'primary' : ''"
              :dark="quick === 'year'"
              :outlined="quick !== 'year'"
              @click="applyQuick('year')"
            >Full Year</v-chip>
          </v-chip-group>
        </div>
        <div class="text-caption mb-3" style="color:#898781">
          Pick one or more months from the dropdown, or use a quick range.
        </div>

        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="filters.path"
              label="URL Path contains"
              placeholder="/example/path"
              prepend-icon="mdi-link"
              clearable
              @keyup.enter="searchPageViews"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="primary"
          :loading="loading"
          @click="searchPageViews"
          :disabled="loading"
        >
          <v-icon left>mdi-magnify</v-icon>
          <span v-if="loading">Searching...</span>
          <span v-else>Search</span>
        </v-btn>
        <v-btn class="ml-5" @click="resetFilters" outlined>
          <v-icon left>mdi-restore</v-icon>
          Reset
        </v-btn>
      </v-card-actions>
    </v-card>

    <div v-if="hasSearched" class="mt-6">
      <div class="d-flex align-center mb-4">
        <h2 class="text-xl font-semibold">Traffic Report</h2>
        <span class="ml-3" style="color:#898781">{{ periodLabel }}</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mr-3" small :disabled="!totalViews" @click="exportToExcel">
          <v-icon left small>mdi-microsoft-excel</v-icon>
          Export to Excel
        </v-btn>
        <v-btn small :disabled="!totalViews" @click="exportToCSV">
          <v-icon left small>mdi-file-export</v-icon>
          Export to CSV
        </v-btn>
      </div>

      <div v-if="loading && totalViews === null" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-3" style="color:#898781">Crunching the numbers for this range…</p>
      </div>

      <div v-else-if="totalViews === 0" class="text-center py-8">
        <p class="text-xl" style="color:#898781">No page views found matching your criteria.</p>
      </div>

      <template v-else-if="totalViews > 0">
        <traffic-summary-panels :data="summaryData" />

        <!-- Raw data: loaded only when the panel is opened -->
        <v-expansion-panels class="mt-3" flat v-model="rawPanel">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-semibold">
              Raw page views ({{ Number(totalViews).toLocaleString() }} records)
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :headers="headers"
                :items="pageViews"
                :options.sync="options"
                :server-items-length="totalViews || 0"
                :loading="rawLoading"
                :footer-props="{ 'items-per-page-options': [50, 100, 250, 500] }"
                disable-sort
                class="elevation-1"
              >
                <template v-slot:[`item.visited_at`]="{ item }">
                  {{ formatDateTime(item.visited_at) }}
                </template>
                <template v-slot:[`item.path`]="{ item }">
                  <a :href="item.path" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">
                    {{ item.path }}
                  </a>
                </template>
                <template v-slot:[`item.referer`]="{ item }">
                  <span class="truncate">{{ item.referer || 'Direct' }}</span>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios';
import Layout from '@/Shared/VuetifyAdminLayout';
import TrafficSummaryPanels from '@/Shared/TrafficSummaryPanels';
import { buildRangePresets } from '@/Shared/rangePresets';

export default {
  layout: Layout,
  components: { TrafficSummaryPanels },
  data() {
    const today = buildRangePresets()[0];
    const currentYear = new Date().getFullYear();
    return {
      currentYear,
      periodYear: currentYear,
      quick: 'today',
      selectedMonths: [],
      filters: {
        startDate: today.start,
        endDate: today.end,
        year: null,
        months: [],
        path: '',
      },
      pageViews: [],
      loading: false,
      rawLoading: false,
      rawPanel: undefined,
      hasSearched: false,
      options: {
        page: 1,
        itemsPerPage: 50,
      },
      summaryData: {},
      headers: [
        { text: 'Time', value: 'visited_at' },
        { text: 'URL Path', value: 'path' },
        { text: 'Referrer', value: 'referer' },
      ],
    };
  },
  computed: {
    yearOptions() {
      // Tracking data starts mid-2025.
      const years = [];
      for (let y = this.currentYear; y >= 2025; y--) years.push(y);
      return years;
    },
    monthsForYear() {
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const last = this.periodYear === this.currentYear ? new Date().getMonth() + 1 : 12;
      // Most recent month first.
      return names.slice(0, last).map((label, i) => ({ label, value: i + 1 })).reverse();
    },
    periodLabel() {
      if (this.selectedMonths.length) {
        const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return [...this.selectedMonths].sort((a, b) => a - b).map(m => names[m - 1]).join(', ') + ' ' + this.periodYear;
      }
      if (this.quick === 'today') return 'Today';
      if (this.quick === 'week') return 'Last 7 days';
      return `Full Year ${this.periodYear}`;
    },
    totalViews() {
      // null while the summary is still loading.
      return this.summaryData.totals ? Number(this.summaryData.totals.views) : null;
    },
  },
  watch: {
    options: {
      handler() {
        if (this.hasSearched && this.rawPanel === 0) {
          this.fetchPage();
        }
      },
      deep: true,
    },
    rawPanel(value) {
      // Fetch the raw rows only the first time the panel is opened.
      if (value === 0 && !this.pageViews.length) {
        this.fetchPage();
      }
    },
  },
  methods: {
    applyQuick(type) {
      this.quick = type;
      this.selectedMonths = [];
      this.filters.year = null;
      this.filters.months = [];

      const iso = d => {
        const p = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
      };
      const now = new Date();
      if (type === 'today') {
        this.filters.startDate = iso(now);
        this.filters.endDate = iso(now);
      } else if (type === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.filters.startDate = iso(weekAgo);
        this.filters.endDate = iso(now);
      } else {
        this.filters.startDate = `${this.periodYear}-01-01`;
        this.filters.endDate = this.periodYear === this.currentYear ? iso(now) : `${this.periodYear}-12-31`;
      }
      this.searchPageViews();
    },
    onMonthsChange() {
      if (!this.selectedMonths.length) {
        this.applyQuick('year');
        return;
      }
      this.quick = null;
      this.filters.startDate = null;
      this.filters.endDate = null;
      this.filters.year = this.periodYear;
      this.filters.months = [...this.selectedMonths].sort((a, b) => a - b);
      this.searchPageViews();
    },
    onYearChange() {
      this.applyQuick('year');
    },
    async searchPageViews() {
      // Stats only; the raw table loads on demand when its panel opens.
      this.hasSearched = true;
      this.pageViews = [];
      this.rawPanel = undefined;
      this.options.page = 1;
      this.summaryData = {};
      await this.fetchSummary();
    },
    async fetchPage() {
      this.rawLoading = true;

      try {
        const response = await axios.get('/admin/analytics/search-page-views', {
          params: {
            ...this.filters,
            page: this.options.page,
            perPage: this.options.itemsPerPage
          }
        });

        this.pageViews = response.data.data;
      } catch (error) {
        console.error('Error searching page views:', error);
      } finally {
        this.rawLoading = false;
      }
    },
    async fetchSummary() {
      this.loading = true;
      try {
        const response = await axios.get('/admin/analytics/traffic-summary', {
          params: { ...this.filters }
        });
        this.summaryData = response.data;
      } catch (error) {
        console.error('Error fetching traffic summary:', error);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      const today = buildRangePresets()[0];
      this.periodYear = this.currentYear;
      this.quick = 'today';
      this.selectedMonths = [];
      this.filters = {
        startDate: today.start,
        endDate: today.end,
        year: null,
        months: [],
        path: '',
      };
      this.pageViews = [];
      this.rawPanel = undefined;
      this.summaryData = {};
      // Land straight back on today's report.
      this.searchPageViews();
    },

    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString();
    },

    filterQueryString() {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key + '[]', v));
        } else if (value) {
          params.append(key, value);
        }
      });
      return params.toString();
    },
    exportToCSV() {
      // The server streams the full filtered result set, not just the
      // rows currently loaded in the table.
      window.location.href = `/admin/analytics/search-export?${this.filterQueryString()}`;
    },
    exportToExcel() {
      // Excel workbook with a Summary sheet (totals, devices, top pages)
      // plus the detail rows.
      window.location.href = `/admin/analytics/search-export-excel?${this.filterQueryString()}`;
    }
  },
  mounted() {
    // Land on today's report immediately (the default filters are Today,
    // which is always pre-warmed, so this renders instantly).
    this.searchPageViews();
  }
};
</script>
<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
